import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';
import { getDashboard, logWeight } from '../lib/api';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X } from 'lucide-react';

export default function InserisciPeso() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useTranslation();
  const { formatNumber, convertWeight, weightUnit, convertWeightToKg } = useUnits();
  
  const todayStr = new Date().toISOString().split('T')[0];
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);
  
  const [loading, setLoading] = useState(true);
  const [dashData, setDashData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1));
  const [editWeight, setEditWeight] = useState(80);
  const [editDecimal, setEditDecimal] = useState(0);
  const [mood, setMood] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [initialScrollDone, setInitialScrollDone] = useState(false);

  // Refs for smooth scroll animation
  const wholeScrollRef = useRef(null);
  const decimalScrollRef = useRef(null);

  const moods = ['😩', '😕', '😐', '😊', '🤩'];
  
  // Generate range based on unit (kg: 20-200, lbs: 44-440)
  const isLbs = weightUnit === 'lbs';
  const wholeRange = isLbs 
    ? Array.from({ length: 397 }, (_, i) => i + 44) // 44-440 lbs
    : Array.from({ length: 181 }, (_, i) => i + 20); // 20-200 kg
  const decimalRange = Array.from({ length: 10 }, (_, i) => i); // 0-9

  // Weekday names based on language
  const getWeekDays = () => {
    const days = {
      it: ['LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB', 'DOM'],
      en: ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'],
      pt: ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB', 'DOM'],
      es: ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'],
      de: ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'],
      fr: ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM']
    };
    return days[language] || days.en;
  };
  const weekDays = getWeekDays();

  // Smooth scroll animation function (iOS-like easing)
  const smoothScrollTo = useCallback((element, targetPosition, duration = 600) => {
    if (!element) return;
    
    const startPosition = element.scrollTop;
    const distance = targetPosition - startPosition;
    const startTime = performance.now();
    
    // iOS-style spring easing function
    const easeOutBack = (t) => {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    };
    
    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutBack(progress);
      
      element.scrollTop = startPosition + (distance * easedProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };
    
    requestAnimationFrame(animateScroll);
  }, []);

  // Initial scroll animation after data loads
  useEffect(() => {
    if (!loading && !initialScrollDone && wholeScrollRef.current && decimalScrollRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        const wholeIndex = wholeRange.indexOf(editWeight);
        if (wholeIndex !== -1) {
          smoothScrollTo(wholeScrollRef.current, wholeIndex * 36, 800);
        }
        smoothScrollTo(decimalScrollRef.current, editDecimal * 36, 800);
        setInitialScrollDone(true);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [loading, initialScrollDone, editWeight, editDecimal, wholeRange, smoothScrollTo]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getDashboard();
        setDashData(res.data);
        // `current_weight` is the most recent logged weight (previous day's
        // log when today isn't logged yet, or today's log so the user can
        // tweak it). Falls back to plan.start_weight on the first ever
        // launch — and only if nothing came back, the hard-coded 80 default.
        const seedKg = res.data?.current_weight ?? res.data?.plan?.start_weight;
        if (seedKg != null) {
          // Convert from kg to display unit, then split into integer + decimal
          // so both scrollers start from the seed weight (e.g. 73.5 →
          // whole=73, decimal=5). Rounding avoids floating-point drift.
          const displayWeight = convertWeight(seedKg);
          const rounded = Math.round(displayWeight * 10) / 10;
          const whole = Math.trunc(rounded);
          const decimal = Math.round((rounded - whole) * 10);
          setEditWeight(whole);
          setEditDecimal(Math.max(0, Math.min(9, decimal)));
        }
      } catch (err) {
        console.error('Error fetching dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [convertWeight]);

  // Get month name based on language
  const getMonthName = (date) => {
    const locale = language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  };

  // Calculate expected weight for selected date
  const getExpectedWeight = () => {
    if (!dashData?.plan) return null;
    const plan = dashData.plan;
    const start = new Date(plan.start_date);
    const end = new Date(plan.end_date);
    const selected = new Date(selectedDate);
    
    const totalDays = (end - start) / (1000 * 60 * 60 * 24);
    if (totalDays <= 0) return null;
    
    const elapsedDays = (selected - start) / (1000 * 60 * 60 * 24);
    const progress = Math.min(1, Math.max(0, elapsedDays / totalDays));
    
    // Use start_weight (not initial_weight) as that's the field name in the plan
    const startWeight = plan.start_weight;
    const targetWeight = plan.target_weight;
    
    if (!startWeight || !targetWeight) return null;
    
    const totalLoss = startWeight - targetWeight;
    const expectedLoss = totalLoss * (1 - Math.exp(-3 * progress));
    const result = startWeight - expectedLoss;
    
    return isNaN(result) ? null : result.toFixed(2);
  };

  // Generate calendar days for the current month view
  const generateCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the day of week for first day (0=Sun, 1=Mon, ...)
    // Convert to Monday-based (0=Mon, 6=Sun)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;
    
    const days = [];
    
    // Add empty cells for days before the 1st
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ empty: true });
    }
    
    // Add actual days
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toISOString().split('T')[0];
      const isToday = dateStr === todayStr;
      const isSelected = dateStr === selectedDate;
      // Only disable future dates
      const isDisabled = date > todayDate;
      
      days.push({
        day,
        dateStr,
        isToday,
        isSelected,
        isDisabled,
        empty: false
      });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();

  const goToPrevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    const nextMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1);
    // Don't go beyond current month
    if (nextMonth <= new Date(todayDate.getFullYear(), todayDate.getMonth(), 1)) {
      setCalendarMonth(nextMonth);
    }
  };

  const canGoNext = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1) <= new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);

  const handleSave = async () => {
    // Convert from display unit to kg for storage
    const displayWeight = editWeight + (editDecimal / 10);
    const weightInKg = convertWeightToKg(displayWeight);
    setSaving(true);
    try {
      await logWeight({
        weight: weightInKg,
        mood: mood || null,
        date: selectedDate
      });
      setSaved(true);
    } catch (err) {
      console.error('Error saving weight:', err);
    } finally {
      setSaving(false);
    }
  };

  const formatSelectedDate = () => {
    const d = new Date(selectedDate);
    const locale = language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US';
    return d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isSelectedToday = selectedDate === todayStr;
  const expectedWeight = getExpectedWeight();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Show success screen after saving
  if (saved) {
    const displayWeight = editWeight + (editDecimal / 10);
    const expWeightDisplay = expectedWeight ? convertWeight(parseFloat(expectedWeight)) : null;
    const diff = expWeightDisplay ? (displayWeight - expWeightDisplay).toFixed(2) : null;
    const status = diff === null ? 'on' : Math.abs(parseFloat(diff)) < 0.1 ? 'on' : parseFloat(diff) < 0 ? 'under' : 'over';
    
    return (
      <div className="min-h-screen pb-20" data-testid="weight-success">
        <div className="px-4 pt-6 pb-4" style={{ paddingTop: 'calc(env(safe-area-inset-top, 24px) + 16px)' }}>
          <h1 className={`font-display text-2xl mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('weightEntry.saved')}
          </h1>
          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
            {t('weightEntry.keepGoing')}
          </p>
        </div>

        <div className="px-4">
          <div className="glass-card p-6 text-center">
            <div className={`inline-flex px-4 py-2 rounded-full text-sm font-medium mb-4 ${
              status === 'under' ? 'bg-green-500/20 text-green-500' :
              status === 'over' ? 'bg-orange-500/20 text-orange-500' :
              'bg-blue-500/20 text-blue-500'
            }`}>
              {status === 'under' ? t('weightEntry.underLine') : status === 'over' ? t('weightEntry.overLine') : t('weightEntry.onLine')}
            </div>

            <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {status === 'under' 
                ? t('weightEntry.underMessage', { kg: Math.abs(parseFloat(diff)) })
                : status === 'over'
                  ? t('weightEntry.overMessage', { kg: diff })
                  : t('weightEntry.onMessage')}
            </p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <p className={`text-xs uppercase mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t('weightEntry.enteredWeight')}</p>
                <p className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {formatNumber(displayWeight, 1)} <span className="text-sm font-normal">{weightUnit}</span>
                </p>
              </div>
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <p className={`text-xs uppercase mb-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{t('weightEntry.theoreticalLine')}</p>
                <p className={`font-display text-2xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {expWeightDisplay ? formatNumber(expWeightDisplay, 1) : '--'} <span className="text-sm font-normal">{weightUnit}</span>
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => navigate('/app/dashboard')} className="btn-primary flex-1">
                Dashboard
              </button>
              <button 
                onClick={() => navigate(`/app/nuovo-post?tipo=peso&peso=${convertWeightToKg(displayWeight)}`)} 
                className="btn-secondary flex-1"
              >
                {t('common.share')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="inserisci-peso-page">
      {/* Header with Cancel */}
      <div className={`flex items-center justify-between px-4 pb-2 ${isDark ? 'bg-[#0A0F1C]' : 'bg-white'}`} style={{ paddingTop: 'calc(env(safe-area-inset-top, 16px) + 12px)' }}>
        <button 
          onClick={() => navigate(-1)}
          className={`p-2 -ml-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
        >
          <X className={`w-6 h-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`} />
        </button>
        <h1 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('weightEntry.title')}
        </h1>
        <div className="w-10" />
      </div>

      {/* Main Card - Scrollable content area */}
      <div className="flex-1 overflow-auto px-4 pb-4">
        <div className="glass-card p-4">
          
          {/* Date Section - Compact */}
          <div className="mb-3">
            <div className="flex items-center justify-between">
              <label className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                {t('weightEntry.date')}
              </label>
              {expectedWeight && (
                <span className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t('weightEntry.theoreticalLine')}: <strong className="text-[#1d3557]">{formatNumber(convertWeight(parseFloat(expectedWeight)), 1)} {weightUnit}</strong>
                </span>
              )}
            </div>
            <button 
              onClick={() => setShowCalendar(!showCalendar)}
              className={`w-full mt-2 p-3 rounded-xl flex items-center justify-between ${
                isDark ? 'bg-white/5' : 'bg-gray-100'
              }`}
              data-testid="date-selector-btn"
            >
              <span className={`font-display text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatSelectedDate()}
              </span>
              {showCalendar ? (
                <ChevronUp className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
              ) : (
                <ChevronDown className={`w-4 h-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
              )}
            </button>

            {/* Collapsible Calendar */}
            {showCalendar && (
              <div className={`mt-2 p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                {/* Month Header with Navigation */}
                <div className="flex items-center justify-between mb-3">
                  <button 
                    onClick={goToPrevMonth}
                    className={`w-7 h-7 rounded-full flex items-center justify-center ${isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <h3 className={`font-display text-sm capitalize ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {getMonthName(calendarMonth)}
                  </h3>
                  <button 
                    onClick={goToNextMonth}
                    disabled={!canGoNext}
                    className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      canGoNext 
                        ? isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'
                        : isDark ? 'bg-white/5 text-white/20' : 'bg-gray-100 text-gray-300'
                    }`}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {weekDays.map(day => (
                    <div 
                      key={day} 
                      className={`text-center text-[10px] font-medium py-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, idx) => (
                    <div key={day.dateStr || `empty-${idx}`} className="aspect-square">
                      {!day.empty && (
                        <button
                          onClick={() => {
                            if (!day.isDisabled) {
                              setSelectedDate(day.dateStr);
                              setShowCalendar(false);
                            }
                          }}
                          disabled={day.isDisabled}
                          className={`w-full h-full rounded-full flex items-center justify-center font-display text-xs transition-all ${
                            day.isSelected
                              ? 'bg-[#3B82F6] text-white'
                              : day.isToday
                                ? isDark ? 'bg-[#E8334A]/20 text-[#E8334A] font-bold' : 'bg-[#E8334A]/10 text-[#E8334A] font-bold'
                                : day.isDisabled
                                  ? isDark ? 'text-white/20' : 'text-gray-300'
                                  : isDark ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {day.day}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Weight Section - Compact */}
          <div className="mb-4">
            <label className={`text-xs font-medium uppercase tracking-wider mb-1 block text-center ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('weightEntry.weight')}
            </label>

            {/* Weight Display */}
            <div className="text-center mb-1">
              <span className="font-display text-4xl font-bold text-[#1d3557]">
                {editWeight}.{editDecimal}
              </span>
              <span className={`text-lg ${isDark ? 'text-white/50' : 'text-gray-500'}`}> {weightUnit}</span>
            </div>

            {/* Drum Picker Container - Smaller */}
            <div className={`flex items-center justify-center rounded-xl overflow-hidden h-36 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
              {/* Whole Number Column */}
              <div className="relative h-full w-24 overflow-hidden">
                <div 
                  ref={wholeScrollRef}
                  className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
                  style={{ scrollBehavior: 'auto' }}
                  onScroll={(e) => {
                    const scrollTop = e.target.scrollTop;
                    const itemHeight = 36;
                    const index = Math.round(scrollTop / itemHeight);
                    if (wholeRange[index] !== undefined && initialScrollDone) {
                      setEditWeight(wholeRange[index]);
                    }
                  }}
                >
                  <div className="h-[54px]"></div>
                  {wholeRange.map(w => (
                    <div 
                      key={w}
                      className={`h-9 flex items-center justify-center snap-center font-display transition-all duration-150 ${
                        editWeight === w 
                          ? 'text-[#1d3557] font-bold text-2xl scale-110'
                          : Math.abs(editWeight - w) === 1
                            ? isDark ? 'text-white/40 text-lg' : 'text-gray-400 text-lg'
                            : isDark ? 'text-white/20 text-base' : 'text-gray-300 text-base'
                      }`}
                    >
                      {w}
                    </div>
                  ))}
                  <div className="h-[54px]"></div>
                </div>
                <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-9 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
              </div>

              {/* Separator */}
              <span className={`font-display text-2xl font-bold ${isDark ? 'text-white/30' : 'text-gray-300'}`}>.</span>

              {/* Decimals Column */}
              <div className="relative h-full w-16 overflow-hidden">
                <div 
                  ref={decimalScrollRef}
                  className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
                  style={{ scrollBehavior: 'auto' }}
                  onScroll={(e) => {
                    const scrollTop = e.target.scrollTop;
                    const itemHeight = 36;
                    const index = Math.round(scrollTop / itemHeight);
                    if (decimalRange[index] !== undefined && initialScrollDone) {
                      setEditDecimal(decimalRange[index]);
                    }
                  }}
                >
                  <div className="h-[54px]"></div>
                  {decimalRange.map(d => (
                    <div 
                      key={d}
                      className={`h-9 flex items-center justify-center snap-center font-display transition-all duration-150 ${
                        editDecimal === d 
                          ? 'text-[#1d3557] font-bold text-2xl scale-110'
                          : Math.abs(editDecimal - d) === 1
                            ? isDark ? 'text-white/40 text-lg' : 'text-gray-400 text-lg'
                            : isDark ? 'text-white/20 text-base' : 'text-gray-300 text-base'
                      }`}
                    >
                      {d}
                    </div>
                  ))}
                  <div className="h-[54px]"></div>
                </div>
                <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-9 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
              </div>
            </div>
          </div>

          {/* Mood Section - Compact */}
          <div className="mb-4">
            <label className={`text-xs font-medium uppercase tracking-wider mb-2 block text-center ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {isSelectedToday ? t('weightEntry.moodToday') : t('weightEntry.moodThatDay')}
            </label>
            <div className="flex justify-center gap-2">
              {moods.map((m) => (
                <button
                  key={m}
                  onClick={() => setMood(m)}
                  className={`w-11 h-11 rounded-xl text-xl flex items-center justify-center transition-all ${
                    mood === m 
                      ? 'bg-[#E8334A]/20 ring-2 ring-[#E8334A]' 
                      : isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button 
              onClick={() => navigate(-1)}
              className="btn-secondary flex-1 py-3"
            >
              {t('common.cancel')}
            </button>
            <button 
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex-1 py-3"
              data-testid="save-weight-btn"
            >
              {saving ? t('weightHistory.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
